import { Injectable } from '@gitlab/needle';
import { Logger, withPrefix } from '@gitlab-org/logging';
import { ConfigService, type NotificationChannel } from '@gitlab-org/config';
import { doNotAwait } from '@gitlab-org/core';
import { UserPersistentStorage } from '@gitlab-org/persistent-storage';
import {
  CLI_INPUT_TYPES,
  defaultInputState,
  isThemeSetting,
  THEME_SETTINGS,
  SettingsInput,
  settingsFooterHint,
  type SettingsCallbacks,
  type SettingsItem,
  type SettingsToggleItem,
  type SettingsSelectorItem,
} from '@gitlab-org/tui';
import type { ControllerApi } from '../../commands/tui/controller_api';
import { ThemeService } from '../../utils/theme_service';
import {
  SlashCommandHandler,
  SlashCommandAction,
  type CommandComponentEntry,
} from '../slash_command_handler';

const NOTIFICATIONS_KEY = 'notifications';
const THEME_KEY = 'theme';
const DEFAULT_NOTIFICATION_CHANNEL: NotificationChannel = 'auto';

// The static shape of each setting; the dynamic field (a toggle's `enabled` or a
// selector's `value`) is filled in at build time by #buildSettingsItems.
type SettingsDefinition = Omit<SettingsToggleItem, 'enabled'> | Omit<SettingsSelectorItem, 'value'>;

const SETTINGS_DEFINITIONS: SettingsDefinition[] = [
  {
    key: 'telemetry',
    label: 'Telemetry',
    description: 'Send anonymous usage data to improve GitLab Duo',
  },
  {
    key: 'showWorkItemsInNewSessions',
    label: 'Show work items in new sessions',
    description: 'Show relevant work items in the welcome card when starting a new session',
  },
  {
    key: NOTIFICATIONS_KEY,
    label: 'Notifications',
    description:
      'System notifications when a session needs attention while the terminal is unfocused',
    options: [
      { value: 'auto', label: 'auto' },
      { value: 'disabled', label: 'disabled' },
    ],
  },
  {
    key: THEME_KEY,
    label: 'Theme',
    description: 'Color theme for the interface; auto follows the terminal background',
    options: THEME_SETTINGS.map((setting) => ({
      value: setting,
      label: setting.replaceAll('-', ' '),
    })),
  },
];

@Injectable(SlashCommandHandler, [ConfigService, UserPersistentStorage, ThemeService, Logger])
export class DefaultSettingsCommandHandler implements SlashCommandHandler<SettingsCallbacks> {
  #configService: ConfigService;

  #userPersistentStorage: UserPersistentStorage;

  #themeService: ThemeService;

  #logger: Logger;

  command = {
    name: '/settings',
    description: 'Open settings',
    action: SlashCommandAction.Settings,
  } as const;

  constructor(
    configService: ConfigService,
    userPersistentStorage: UserPersistentStorage,
    themeService: ThemeService,
    logger: Logger,
  ) {
    this.#configService = configService;
    this.#userPersistentStorage = userPersistentStorage;
    this.#themeService = themeService;
    this.#logger = withPrefix(logger, '[SettingsCommandHandler]');
  }

  async execute(api: ControllerApi): Promise<void> {
    await api.ensureInitialized();
    this.#openSettings(api);
  }

  getComponent(api: ControllerApi): CommandComponentEntry<SettingsCallbacks> {
    return {
      inputType: CLI_INPUT_TYPES.SETTINGS,
      component: SettingsInput,
      footerHint: settingsFooterHint,
      callbacks: {
        onToggle: (key: string) => this.#handleToggle(api, key),
        onSelect: (key: string, value: string) => this.#handleSelect(api, key, value),
        onHighlight: (key: string, value: string) => this.#handleHighlight(key, value),
        onClose: () => this.#closeSettings(api),
      },
    };
  }

  #openSettings(api: ControllerApi): void {
    this.#logger.info('Opening settings');

    api.mutateState((state) => ({
      ...state,
      input: {
        inputType: CLI_INPUT_TYPES.SETTINGS,
        items: this.#buildSettingsItems(),
        selectedIndex: 0,
      },
    }));
  }

  #closeSettings(api: ControllerApi): void {
    this.#logger.info('Closing settings');
    api.mutateState((state) => ({ ...state, input: defaultInputState }));
  }

  #handleToggle(api: ControllerApi, key: string): void {
    const item = SETTINGS_DEFINITIONS.find((d) => d.key === key);
    if (!item) return;

    const newValue = !this.#getCurrentValue(key);

    // Update ConfigService immediately
    this.#setConfigValue(key, newValue);

    // Persist asynchronously
    doNotAwait(this.#persistSetting(key, newValue));

    if (key === 'showWorkItemsInNewSessions') {
      this.#syncWelcomeCardVisibility(api, newValue);
    }

    // Update TUI state
    this.#refreshSettingsItems(api);
  }

  /**
   * Applies the "Show work items in new sessions" toggle to the current session.
   *
   * - Disabling hides the welcome card immediately.
   * - Enabling brings the card straight back only when the contextual prompts
   *   were already fetched (card was enabled at startup and toggled off in this
   *   session). When the prompts were never fetched (card was disabled at
   *   startup), showing the card now would mean a stuck loader, so enabling
   *   takes effect in the next session, where the prompts are fetched again.
   */
  #syncWelcomeCardVisibility(api: ControllerApi, enabled: boolean): void {
    api.mutateState((state) => {
      if (!enabled) {
        return { ...state, welcomeCardEnabled: false };
      }
      if (state.welcomePrompts !== undefined) {
        return { ...state, welcomeCardEnabled: true };
      }
      return state;
    });
  }

  #handleSelect(api: ControllerApi, key: string, value: string): void {
    if (key === NOTIFICATIONS_KEY) {
      const channel: NotificationChannel = value === 'disabled' ? 'disabled' : 'auto';
      this.#configService.set('notifications', { channel });
      doNotAwait(this.#persistNotifications(channel));
    } else if (key === THEME_KEY && isThemeSetting(value)) {
      // Applies to the running TUI immediately and persists asynchronously.
      this.#themeService.setSetting(value);
    } else {
      return;
    }

    this.#refreshSettingsItems(api);
  }

  /**
   * Live-previews a theme option while it is highlighted in the settings
   * selector. The TUI fires this with the committed value when the option list
   * is dismissed, which undoes the preview. Other settings have no preview.
   */
  #handleHighlight(key: string, value: string): void {
    if (key !== THEME_KEY || !isThemeSetting(value)) return;
    this.#themeService.previewSetting(value);
  }

  /**
   * Re-renders the settings list in place after a toggle/select, preserving the current
   * selection. No-op if the user has navigated away from the settings input in the meantime.
   */
  #refreshSettingsItems(api: ControllerApi): void {
    api.mutateState((state) => {
      if (state.input.inputType !== CLI_INPUT_TYPES.SETTINGS) return state;
      return {
        ...state,
        input: {
          ...state.input,
          items: this.#buildSettingsItems(),
        },
      };
    });
  }

  #buildSettingsItems(): SettingsItem[] {
    return SETTINGS_DEFINITIONS.map((def): SettingsItem => {
      if (def.options) {
        const item: SettingsItem = { ...def, value: this.#getSelectorValue(def.key) };
        if (def.key === THEME_KEY && this.#themeService.isEnvOverridden()) {
          item.disabled = true;
          item.description =
            'Set by the GITLAB_DUO_CLI_THEME environment variable; unset it to change the theme here';
        }
        return item;
      }
      return { ...def, enabled: this.#getCurrentValue(def.key) };
    });
  }

  #getSelectorValue(key: string): string {
    if (key === THEME_KEY) {
      return this.#themeService.getSetting();
    }
    return this.#getNotificationChannel();
  }

  #getNotificationChannel(): NotificationChannel {
    return this.#configService.get('notifications')?.channel ?? DEFAULT_NOTIFICATION_CHANNEL;
  }

  async #persistNotifications(channel: NotificationChannel): Promise<void> {
    try {
      await this.#userPersistentStorage.set('notifications', { channel });
    } catch (error) {
      this.#logger.error('Failed to persist notifications setting', error);
    }
  }

  #getCurrentValue(key: string): boolean {
    if (key === 'telemetry') {
      return this.#configService.get('telemetry')?.enabled ?? true;
    }
    if (key === 'showWorkItemsInNewSessions') {
      return this.#configService.get('showWorkItemsInNewSessions') ?? true;
    }
    return false;
  }

  #setConfigValue(key: string, value: boolean): void {
    if (key === 'telemetry') {
      this.#configService.set('telemetry.enabled', value);
    } else if (key === 'showWorkItemsInNewSessions') {
      this.#configService.set('showWorkItemsInNewSessions', value);
    }
  }

  async #persistSetting(key: string, value: boolean): Promise<void> {
    try {
      if (key === 'telemetry') {
        await this.#userPersistentStorage.set('telemetry', { enabled: value });
      } else if (key === 'showWorkItemsInNewSessions') {
        await this.#userPersistentStorage.set('showWorkItemsInNewSessions', { enabled: value });
      }
    } catch (error) {
      this.#logger.error(`Failed to persist ${key} setting`, error);
    }
  }
}
